import dayjs from 'dayjs'
import { getIn } from 'formik'
import { camelCase } from 'lodash'
import { mapFields } from '../common/forms/general/fields'
import { projectResponseFields } from '../common/response/project'

/**
 * @param {File[]} files
 * @param {Object} entity project or property
 * @param {string} module project or property
 * @param {string} collection photo or ground_plan
 * @returns {FormData}
 */
export function handleFilesFormData(files, entityData, module, collection) {
    const { parentInternalAppId, city, widerArea } = entityData
    const altTag = `${city?.name ?? ''}, ${widerArea?.name ?? ''}`
    const locales = JSON.parse(localStorage.getItem('translationLangs')) ?? []

    const translationsArray = locales.map((lang) => ({
        locale: lang,
        fileDescription: altTag,
    }))

    const formData = new FormData()

    files.forEach((file, index) => {
        formData.append(`file_${index}`, file)
    })

    formData.append('translations', JSON.stringify(translationsArray))
    formData.append('parent_internal_app_id', parentInternalAppId)
    formData.append('module', module)
    formData.append('collection', collection)
    formData.append('file_sync', 1)

    return formData
}

/**
 *
 * @param {Photo} a The first element for comparison.
 * @param {Photo} b The second element for comparison.
 * @returns {number} see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort
 */
export function priorityCompareFn(a, b) {
    if (!a.priority && b.priority) return 1
    if (a.priority && !b.priority) return -1
    if (a.priority < b.priority) return -1
    if (a.priority > b.priority) return 1
    return 0
}

export function embedYotubeVideo(value) {
    return `https://www.youtube.com/embed/${getVideoId(value)}`
}

/**
 *
 * @param {string} rawUrl can either be full youtube link or just youtube id, ex. https://www.youtube.com/watch?v={ID} or ID
 * @returns youtube video ID
 */
export function getVideoId(rawUrl) {
    try {
        const url = new URL(rawUrl)
        return url.searchParams.get('v')
    } catch (error) {
        return rawUrl
    }
}

/**
 * Check with loose equality if location sync type is set to approximate location (=2)
 *
 * @param Object formValue
 * @returns boolean if map location sync type is 2
 */
export function isApproxLocationSyncType(formValue) {
    if (!formValue) {
        return undefined
    }

    const lookupKey = mapFields.MAP_LOCATION_SYNC_TYPE
    // Two keys are defined because payload and response field have different naming conventions
    const lookupKeys = [lookupKey, camelCase(lookupKey)]

    // eslint-disable-next-line eqeqeq
    return lookupKeys.some((key) => getIn(formValue, key) == 2)
}

/**
 * Coerce lat lng coordinates to number
 *
 * @param {Object} entity - can be property or project
 * @returns lat lng coerced coordinates
 */
export function coerceLatLng(entity) {
    if (!entity) {
        return undefined
    }

    return {
        lat: +entity[projectResponseFields.MAP_LATITUDE],
        lng: +entity[projectResponseFields.MAP_LONGITUDE],
    }
}

/**
 * Normalizes specific fields from backend format (dot as decimal) to display format (comma as decimal)
 * @param {Object} data - The data object containing fields to normalize
 * @param {Array<string>} fields - Array of field names to normalize
 * @returns {Object} Object with normalized fields only
 */
export function normalizeDecimalFields(data, fields) {
    if (!data) return {}

    const normalized = {}

    fields.forEach((field) => {
        if (data[field]) {
            normalized[field] = data[field].toString().replace(/\./g, ',')
        }
    })

    return normalized
}

/**
 * Denormalizes specific fields from display format (comma as decimal) to backend format (dot as decimal)
 * @param {Object} data - The data object containing fields to denormalize
 * @param {Array<string>} fields - Array of field names to denormalize
 * @returns {Object} Object with denormalized fields only
 */
export function denormalizeDecimalFields(data, fields) {
    if (!data) return {}

    const denormalized = {}
    
    fields.forEach((field) => {
        if (data[field]) {
            denormalized[field] = convertNumeralToFloatString(data[field])
        }
    })

    return denormalized
}

/**
 * Converts a numeral string formatting to a float string.
 * Removes thousand separators (dots) and replaces decimal separator (comma) with dot.
 * @param {string} value - The input string (e.g., "1.234,56").
 * @returns {string} The converted string ready for parseFloat (e.g., "1234.56").
 */
export function convertNumeralToFloatString(value) {
    if (!value) return value

    const strValue = value.toString()

    // has comma case: remove thousands separators (dots), replace decimal separator (comma) with dot
    if (strValue.includes(',')) {
        return strValue.replace(/\./g, '').replace(/,/g, '.')
    }

    // no comma case: remove thousands separators (dots) if any
    return strValue.replace(/\./g, '')
}

/**
 * @param {Object} entity Project or property
 * @returns entity's translated title or default fallback
 */
export const findEntityTitle = (entity, field, appLocale = localStorage.getItem('i18nextLng') ?? 'hr') => {
    const translatedTitle = entity?.translations?.[appLocale]?.[field]
    const backupTitle = [entity?.propertySubType?.name, entity?.propertyService?.name, entity?.city?.name, entity?.widerArea?.name]
        .filter(Boolean)
        .join(', ')

    if (Boolean(translatedTitle) && translatedTitle !== '') {
        return translatedTitle
    }

    return entity?.[field] ?? backupTitle
}

export const generateLocationData = (item) => {
    if (!item) return

    const city = item.city?.name ?? ''
    const country = item.country?.name ?? ''

    return `${item.street ? item.street + ', ' : ''}${item.streetNumber ? item.streetNumber + ', ' : ''}${city}${city && country ? ', ' : ''}${country}`
}

export const changeTranslationObject = (object, title, description, offer_description) => {
    const translations = []

    const locales = Object.keys(object?.[title])

    locales.forEach((locale) => {
        translations.push(
            offer_description
                ? {
                      locale,
                      [title]: object?.[title][locale],
                      [description]: object?.[description][locale],
                      [offer_description]: object?.[offer_description][locale],
                  }
                : {
                      locale,
                      [title]: object?.[title][locale],
                      [description]: object?.[description][locale],
                  }
        )
    })

    return translations
}

/**
 * Recursive function that returns either a single value or a list of values
 * @param {Object} company
 * @returns string value or list of string values
 */
export function resolveValueByType(data, accessor) {
    if (data?.id) {
        return data.id.toString() ?? ''
    }

    if (data?.length > 0) {
        if (accessor) {
            return data.map((dataItem) => ({ [accessor]: resolveValueByType(dataItem) }))
        }

        return data.map(resolveValueByType)
    }

    return [{ [accessor]: '' }]
}

/**
 * Formats a number to have two decimal places and applies the 'hr-HR' locale.
 *
 * @param {number} number - The number to be formatted.
 * @returns {string} - The formatted number as a string with two decimal places and 'hr-HR' locale.
 * @throws {Error} - If the input is not a valid number or if it's not provided.
 */
export function formatNumberWithTwoDecimals(number) {
    if (typeof number !== 'number' || isNaN(number)) {
        return ''
    }

    return number.toLocaleString('hr-HR', { minimumFractionDigits: 2 })
}

export const formatDateTime = (date, showTime = false) => {
    const formatString = showTime ? 'DD.MM.YYYY. HH:mm' : 'DD.MM.YYYY.'
    const formatedDate = date && dayjs(date)?.format(formatString)
    return formatedDate
}

export const getUrlBase = (key) => {
    if (!key) return

    let url

    switch (key) {
        case 'projects':
            url = 'project'
            break
        case 'properties':
            url = 'property'
            break
        case 'contacts':
            url = 'contact'
            break
        case 'companies':
            url = 'company'
            break
        case 'activities':
            url = 'activity'
            break
        case 'documents':
            url = 'document'
            break
        case 'reports':
            url = 'report'
            break
        case 'intermediary-diary':
            url = 'intermediaryDiary'
            break
        case 'evidence-diary':
            url = 'evidenceDiary'
            break
        case 'admin':
            url = 'admin'
            break
        default:
            console.error(`There is no page with this ${key}`)
    }

    return url
}

export const convertDateStringToTimeStamp = (strDate) => {
    const date = Date.parse(strDate)
    return date
}
